/**
 * What the Files pane and the page-wide drop overlay accept — picker-fix wave F1 (Q1).
 *
 * The picker stages files ONLY through `host.stageFiles(files, 'framepicker')` (seam S5), and it
 * stages videos only: a PDF dropped on the Frame picker is not "a video with no preview", it is a
 * mistake the user should hear about (`COPY.onlyVideos`). MIME type first, extension second, the
 * same rule `root/source.ts#isVideoUpload` applies to already-staged entries.
 *
 * No React, no DOM beyond the `File`/`FileList` shapes, so a spec can feed plain objects.
 */
import { ANIMATED_IMAGE_PICKER } from '../picker-gate';
import { PICKER_VIDEO_EXTENSIONS, extensionOf, isAnimatedImageName } from '../root/source';

export interface PickedFiles {
    videos: File[];
    /** How many of the offered files were refused (0 = nothing to say). */
    rejected: number;
    /**
     * Round 6 (UX#19): the extensions of the refused files that ARE videos -- a `.mov` on the MP4
     * page -- so the notice can name the page that takes them instead of "only MP4 videos open here".
     * Empty when every refusal was a non-video.
     */
    rejectedVideos: string[];
    /** Review 7 P1-08: the refused files themselves (name, MIME type), so the notice can name one and say why. */
    rejectedFiles?: Array<{ name: string; type: string }>;
    /**
     * R14 (slice C, BUG-003): the refused files that are ANIMATED IMAGES (GIF / WebP / APNG, by MIME type or extension), with
     * their names, so the notice can name the page whose picker opens them ("GIF files open in the Frame picker on GIF to
     * JPG.") or, with `ANIMATED_IMAGE_PICKER` off, what the converter that exists delivers. Present only when there is one
     * (the existing rows compare the whole object).
     */
    rejectedAnimated?: Array<{ name: string; ext: string }>;
}

/** R14 (BUG-003): is this file an animated image the picker can open -- `image/gif|webp|apng`, or the extension? */
export function isAnimatedImageFile(file: Pick<File, 'name' | 'type'> | null | undefined): boolean {
    if (!file) return false;
    return isAnimatedImageName(file.name, typeof file.type === 'string' ? file.type : null);
}

/** A video by MIME type first and extension second; R14 (BUG-003): or an animated image, while the switch is on. */
export function isVideoFile(file: Pick<File, 'name' | 'type'> | null | undefined, animated: boolean = ANIMATED_IMAGE_PICKER): boolean {
    if (!file) return false;
    if (typeof file.type === 'string' && file.type.startsWith('video/')) return true;
    if (PICKER_VIDEO_EXTENSIONS.has(extensionOf(file.name))) return true;
    return animated && isAnimatedImageFile(file);
}

/** The page's list, normalised the way `FileValidationService.validate` compares: lowercase, no dot, no blanks. */
export function normalizeAccepted(accepted: readonly string[] | null | undefined): string[] {
    return (accepted ?? []).map((e) => String(e).trim().toLowerCase().replace(/^\./, '')).filter(Boolean);
}

/**
 * Does the PAGE take this file? (R9b.) With an accepted list the extension decides -- exactly the
 * uploader's rule (`FileValidationService.validate`), so nothing the picker lets through is refused
 * downstream into the hidden Quick convert banner. Without one, the picker's own video test.
 */
export function isAcceptedFile(file: Pick<File, 'name' | 'type'> | null | undefined, accepted?: readonly string[] | null): boolean {
    if (!file) return false;
    const list = normalizeAccepted(accepted);
    if (list.length === 0) return isVideoFile(file);
    return list.includes(extensionOf(file.name));
}

/**
 * Round 9 (L3, R8 #4): EVERY video xconvert takes -- the picker's own list with the page's folded in --
 * sorted, lower-case, without dots. The picker admits all of them (the Studio's O3 / S2 rule: a container
 * the browser cannot play is converted in the background through the edge's `preview` rendition, which
 * the picker's ladder already plays), so a `.mov` on the MP4 page is a file, not a four-second notice.
 */
export function everyVideoExtension(pageAccepted?: readonly string[] | null): string[] {
    return Array.from(new Set([...PICKER_VIDEO_EXTENSIONS, ...normalizeAccepted(pageAccepted)])).sort();
}

/** The `<input accept>` for the page's list (`.mp4,.m4v`), or `video/*` when the page has none. */
export function acceptAttribute(accepted?: readonly string[] | null): string {
    const list = normalizeAccepted(accepted);
    return list.length ? list.map((e) => `.${e}`).join(',') : 'video/*';
}

/** Split an input's / a drop's files into the videos to stage and the count to complain about. */
export function pickVideos(list: ArrayLike<File> | Iterable<File> | null | undefined, accepted?: readonly string[] | null): PickedFiles {
    const files: File[] = list ? Array.from(list as ArrayLike<File>) : [];
    const videos = files.filter((f) => isAcceptedFile(f, accepted));
    const refused = files.filter((f) => !isAcceptedFile(f, accepted));
    // R14 (BUG-003): an animated image is reported as such (whatever the switch says), never as a wrong video container.
    const animated = refused.filter(isAnimatedImageFile).map((f) => ({ name: f.name, ext: extensionOf(f.name) || String(f.type).replace(/^image\//, '') }));
    const rejectedVideos = refused.filter((f) => !isAnimatedImageFile(f) && isVideoFile(f)).map((f) => extensionOf(f.name)).filter(Boolean);
    const out: PickedFiles = { videos, rejected: files.length - videos.length, rejectedVideos };
    // Review 7 P1-08: the refused files ride along for the notice -- NON-enumerable, so the rows and specs that compare whole
    // PickedFiles objects (the field-absent rule above) see the same shape as before.
    if (refused.length) Object.defineProperty(out, 'rejectedFiles', { value: refused.map((f) => ({ name: f.name, type: String(f.type || '') })), enumerable: false });
    if (animated.length) out.rejectedAnimated = animated;
    return out;
}

/** Does a drag carry files at all? (Text and links dragged over the page never show the overlay.) */
export function dragHasFiles(dataTransfer: Pick<DataTransfer, 'types'> | null | undefined): boolean {
    const types = dataTransfer?.types;
    if (!types) return false;
    for (let i = 0; i < types.length; i++) if (types[i] === 'Files') return true;
    return false;
}
